// ============================================================================
// DOC VIEWER OVERVIEW TAB
// Reusable document viewer that renders inline inside WorkItemDetailModal.
// Supports: PDF (native), images, Office docs, and fallback list.
// Can display work-item attachments OR a direct URL passed via props.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileText, Download, ChevronRight, Loader2, AlertCircle,
  Image as ImageIcon, FileSpreadsheet, Presentation, File,
  Maximize2, Minimize2, X, ZoomIn, ZoomOut, RotateCw,
  ExternalLink, Paperclip
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

// ============================================================================
// TYPES
// ============================================================================

export interface DocViewerFile {
  id: string;
  name: string;
  url: string;
  type?: string;       // MIME type (application/pdf, image/png, etc.)
  size?: number;
  size_formatted?: string;
  /** Optional uploader info */
  uploaded_by?: string;
  uploaded_at?: string;
}

export interface DocViewerOverviewTabProps {
  /** Work item to fetch attachments for */
  workItemId?: string;
  /** Direct file list (skip fetching attachments) */
  files?: DocViewerFile[];
  /** Single direct URL to preview immediately */
  directUrl?: string;
  directFileName?: string;
  directFileType?: string;
  /** Whether the tab is currently active (lazy loading) */
  isActive?: boolean;
  isDark?: boolean;
  /** Show basic work item info alongside the viewer */
  showItemInfo?: boolean;
  workItem?: { title?: string; description?: string; item_number?: string; item_type?: string };
  /** Callback when user opens a file externally */
  onOpenExternal?: (file: DocViewerFile) => void;
  /** Filter attachments by category (e.g. 'document', 'photo_*') */
  categoryFilter?: string;
  /** Height of the viewer area (default: 'calc(100vh - 320px)') */
  viewerHeight?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const getToken = () => authService.getTokens()?.access_token || '';

function getExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

function getMimeCategory(file: { name: string; type?: string }): 'pdf' | 'image' | 'office' | 'text' | 'other' {
  const mime = file.type?.toLowerCase() || '';
  const ext = getExtension(file.name);

  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (mime.includes('word') || mime.includes('spreadsheet') || mime.includes('presentation') ||
      mime.includes('officedocument') || mime.includes('opendocument') ||
      ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'].includes(ext)) return 'office';
  if (mime.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'html', 'css', 'js', 'ts'].includes(ext)) return 'text';
  return 'other';
}

function getFileIcon(file: { name: string; type?: string }) {
  const cat = getMimeCategory(file);
  switch (cat) {
    case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
    case 'image': return <ImageIcon className="w-5 h-5 text-green-500" />;
    case 'office': {
      const ext = getExtension(file.name);
      if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-emerald-600" />;
      if (['ppt', 'pptx', 'odp'].includes(ext)) return <Presentation className="w-5 h-5 text-orange-500" />;
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    case 'text': return <FileText className="w-5 h-5 text-gray-500" />;
    default: return <File className="w-5 h-5 text-gray-400" />;
  }
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ============================================================================
// INLINE PDF VIEWER
// ============================================================================

function PdfViewer({ url, isDark, height }: { url: string; isDark: boolean; height: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 h-full ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <AlertCircle className="w-10 h-10 text-amber-500" />
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Unable to render PDF inline</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
        >
          <ExternalLink className="w-4 h-4" /> Open in New Tab
        </a>
      </div>
    );
  }

  return (
    <iframe
      src={`${url}#toolbar=1&navpanes=1`}
      className="w-full rounded-lg border-0"
      style={{ height }}
      title="PDF Viewer"
      onError={() => setError(true)}
    />
  );
}

// ============================================================================
// INLINE IMAGE VIEWER
// ============================================================================

function ImageViewer({ url, fileName, isDark, height }: { url: string; fileName: string; isDark: boolean; height: string }) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  return (
    <div className={`relative flex flex-col rounded-lg overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`} style={{ height }}>
      {/* Toolbar */}
      <div className={`flex items-center gap-1 px-3 py-1.5 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <button onClick={() => setZoom(z => Math.min(z + 0.25, 3))} className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700`} title="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))} className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700`} title="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <button onClick={() => setRotation(r => (r + 90) % 360)} className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700`} title="Rotate">
          <RotateCw className="w-4 h-4" />
        </button>
        <span className={`text-xs ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{Math.round(zoom * 100)}%</span>
      </div>
      {/* Image */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        <img
          src={url}
          alt={fileName}
          className="max-w-full max-h-full object-contain transition-transform duration-200"
          style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
          draggable={false}
        />
      </div>
    </div>
  );
}

// ============================================================================
// TEXT VIEWER
// ============================================================================

function TextViewer({ url, isDark, height }: { url: string; isDark: boolean; height: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
        if (res.ok) setContent(await res.text());
      } catch {} finally { setLoading(false); }
    })();
  }, [url]);

  if (loading) return <div className="flex items-center justify-center" style={{ height }}><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <pre
      className={`p-4 rounded-lg overflow-auto text-xs font-mono whitespace-pre-wrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-800'}`}
      style={{ height, maxHeight: height }}
    >
      {content || 'Empty file'}
    </pre>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DocViewerOverviewTab({
  workItemId,
  files: propFiles,
  directUrl,
  directFileName,
  directFileType,
  isActive = true,
  isDark: isDarkProp,
  showItemInfo = false,
  workItem,
  onOpenExternal,
  categoryFilter,
  viewerHeight = 'calc(100vh - 320px)',
}: DocViewerOverviewTabProps) {
  const { currentTheme } = useTheme();
  const isDark = isDarkProp ?? currentTheme.mode === 'dark';

  const [files, setFiles] = useState<DocViewerFile[]>(propFiles || []);
  const [selectedFile, setSelectedFile] = useState<DocViewerFile | null>(null);
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  // Build initial file from directUrl prop
  const directFile = useMemo((): DocViewerFile | null => {
    if (!directUrl) return null;
    return {
      id: 'direct',
      name: directFileName || 'Document',
      url: directUrl,
      type: directFileType,
    };
  }, [directUrl, directFileName, directFileType]);

  // Fetch attachments from work item if no files provided
  useEffect(() => {
    if (!isActive || hasLoadedRef.current) return;
    if (propFiles || directFile) {
      hasLoadedRef.current = true;
      const allFiles = [...(propFiles || [])];
      if (directFile) allFiles.unshift(directFile);
      setFiles(allFiles);
      if (allFiles.length > 0 && !selectedFile) setSelectedFile(allFiles[0]);
      return;
    }
    if (!workItemId) return;

    hasLoadedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        let url = `/api/v1/work-items/${workItemId}/attachments`;
        if (categoryFilter) url += `?category=${encodeURIComponent(categoryFilter)}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            const mapped: DocViewerFile[] = (data.data || []).map((att: {
              id: string;
              file_name: string;
              download_url?: string;
              file_path?: string;
              file_type?: string;
              file_size?: number;
              file_size_formatted?: string;
              uploader_name?: string;
              created_at?: string;
            }) => ({
              id: att.id,
              name: att.file_name,
              url: att.download_url || att.file_path,
              type: att.file_type,
              size: att.file_size,
              size_formatted: att.file_size_formatted,
              uploaded_by: att.uploader_name,
              uploaded_at: att.created_at,
            }));
            setFiles(mapped);
            if (mapped.length > 0) setSelectedFile(mapped[0]);
          }
        }
      } catch (err) {
        if (!isSilentFetchError(err)) console.error('Failed to load attachments:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isActive, workItemId, propFiles, directFile, categoryFilter, selectedFile]);

  // Update selected when prop files change
  useEffect(() => {
    if (propFiles) {
      setFiles(propFiles);
      if (propFiles.length > 0 && !selectedFile) setSelectedFile(propFiles[0]);
    }
  }, [propFiles, selectedFile]);

  const handleSelectFile = useCallback((file: DocViewerFile) => {
    setSelectedFile(file);
  }, []);

  const handleDownload = useCallback((file: DocViewerFile) => {
    const a = document.createElement('a');
    a.href = file.url;
    a.download = file.name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  // ── Render viewer based on file type ──
  const renderViewer = (file: DocViewerFile) => {
    const category = getMimeCategory(file);

    switch (category) {
      case 'pdf':
        return <PdfViewer url={file.url} isDark={isDark} height={viewerHeight} />;
      case 'image':
        return <ImageViewer url={file.url} fileName={file.name} isDark={isDark} height={viewerHeight} />;
      case 'text':
        return <TextViewer url={file.url} isDark={isDark} height={viewerHeight} />;
      case 'office':
        // Office docs cannot be previewed inline — show download prompt
        return (
          <div
            className={`flex flex-col items-center justify-center gap-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}
            style={{ height: viewerHeight }}
          >
            {getFileIcon(file)}
            <div className="text-center">
              <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{file.name}</p>
              {file.size_formatted && (
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{file.size_formatted}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDownload(file)}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 ${
                  isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                <Download className="w-4 h-4" /> Download
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div
            className={`flex flex-col items-center justify-center gap-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}
            style={{ height: viewerHeight }}
          >
            <File className="w-12 h-12 text-gray-400" />
            <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{file.name}</p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Preview not available for this file type</p>
            <button
              onClick={() => handleDownload(file)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download File
            </button>
          </div>
        );
    }
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className={`w-6 h-6 animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
        <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading documents...</span>
      </div>
    );
  }

  // ── Empty state ──
  if (files.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <Paperclip className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-sm font-medium">No documents attached</p>
        <p className="text-xs mt-1">Upload documents via the Attachments tab</p>
      </div>
    );
  }

  const showSidebar = files.length > 1;

  return (
    <div className="space-y-4">
      {/* Optional item info header */}
      {showItemInfo && workItem && (
        <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            {workItem.item_number && (
              <span className={`text-xs px-2 py-0.5 rounded font-mono ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {workItem.item_number}
              </span>
            )}
            <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{workItem.title}</span>
          </div>
          {workItem.description && (
            <p className={`text-xs mt-1.5 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{workItem.description}</p>
          )}
        </div>
      )}

      {/* Viewer layout */}
      <div className={showSidebar ? 'flex gap-4' : ''}>
        {/* File list sidebar */}
        {showSidebar && (
          <div className={`w-56 flex-shrink-0 rounded-lg border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-3 py-2 text-xs font-semibold border-b ${isDark ? 'text-gray-400 border-gray-700 bg-gray-800/50' : 'text-gray-500 border-gray-200 bg-gray-50'}`}>
              Documents ({files.length})
            </div>
            <div className="overflow-y-auto max-h-[500px]">
              {files.map(file => {
                const isSelected = selectedFile?.id === file.id;
                return (
                  <button
                    key={file.id}
                    onClick={() => handleSelectFile(file)}
                    className={`w-full text-left px-3 py-2.5 flex items-start gap-2 border-b transition-colors ${
                      isSelected
                        ? isDark ? 'bg-blue-900/30 border-blue-800/50' : 'bg-blue-50 border-blue-100'
                        : isDark ? 'hover:bg-gray-700/50 border-gray-700/50' : 'hover:bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">{getFileIcon(file)}</div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-medium truncate ${
                        isSelected
                          ? isDark ? 'text-blue-300' : 'text-blue-700'
                          : isDark ? 'text-gray-200' : 'text-gray-800'
                      }`}>
                        {file.name}
                      </p>
                      {(file.size_formatted || file.uploaded_by) && (
                        <p className={`text-[10px] mt-0.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {file.size_formatted}{file.uploaded_by ? ` · ${file.uploaded_by}` : ''}
                        </p>
                      )}
                    </div>
                    {isSelected && <ChevronRight className={`w-3 h-3 flex-shrink-0 mt-1 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main viewer area */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          {selectedFile && (
            <div className={`flex items-center justify-between px-3 py-2 mb-2 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center gap-2 min-w-0">
                {getFileIcon(selectedFile)}
                <span className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedFile.name}</span>
                {selectedFile.size_formatted && (
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({selectedFile.size_formatted})</span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => handleDownload(selectedFile)}
                  className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                {onOpenExternal && (
                  <button
                    onClick={() => onOpenExternal(selectedFile)}
                    className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                    title="Open externally"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Viewer */}
          {selectedFile ? renderViewer(selectedFile) : (
            <div className={`flex items-center justify-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`} style={{ height: viewerHeight }}>
              Select a document to preview
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
